<script setup lang="ts">
import { useChat } from '@/composables/useChat'
import { useThreads } from '@/composables/useThreads'
import { useFeedback } from '@/composables/useFeedback'
import ChatHeader from './ChatHeader.vue'
import MessageList from './MessageList.vue'
import ChatInput from './ChatInput.vue'
import ConnectionError from './ConnectionError.vue'
import WelcomeScreen from './WelcomeScreen.vue'
import FeedbackModal from './FeedbackModal.vue'
import type { ChatMessage } from '@/types'

defineProps<{
  sourcesAvailable?: boolean
  sourcesOpen?: boolean
}>()
const emit = defineEmits<{ 'toggle-sources': [] }>()

const { items, isStreaming, isThinking, connectionError,
  nodeStatus, sendMessage, retryLastMessage, abortStream } = useChat()
const { activeThreadId } = useThreads()
const { openFeedback } = useFeedback()

function onSend(text: string) {
  sendMessage(text)
}

function onFeedback(payload: { message: ChatMessage; sentiment: 'up' | 'down' }) {
  openFeedback(payload.message, payload.sentiment)
}
</script>

<template>
  <div class="flex flex-col min-w-[200px] overflow-hidden">
    <ChatHeader
      :sources-available="sourcesAvailable"
      :sources-open="sourcesOpen"
      @toggle-sources="emit('toggle-sources')"
    />
    <WelcomeScreen v-if="!activeThreadId && items.length === 0" />
    <MessageList
      v-else
      :items="items"
      :streaming="isStreaming"
      class="flex-1"
      @retry="retryLastMessage"
      @feedback="onFeedback"
    />
    <ConnectionError v-if="connectionError" @retry="retryLastMessage" />
    <ChatInput
      :streaming="isStreaming"
      :thinking="isThinking"
      :node-status="nodeStatus"
      @send="onSend"
      @abort="abortStream"
    />
    <FeedbackModal />
  </div>
</template>
